import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout";
import DiploksBurnMeter from "../components/DiploksBurnMeter";
import { requestDiploksConsent } from "../components/DiploksConsentModal";
import { showLowBalance } from "../components/DiploksLowBalanceDialog";
import { consume } from "../lib/diploksClient";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Coins, Plus, ArrowUpRight, ShieldCheck, ScrollText, Sparkles, Clock,
  Trophy, Info, Wand2,
} from "lucide-react";

/**
 * /dashboard/diploks/personal/wallet
 * ----------------------------------------------------------------------
 * Universal personal Diploks wallet — visible to delegate / chair /
 * faculty / parent / volunteer / judge / event_manager / school_admin /
 * org_admin / super_admin.  Sponsor / partner_institution / observer
 * are gated server-side AND by ProtectedRoute role-list.
 */
export default function PersonalDiploksWalletPage() {
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState({ rows: [], total: 0 });

  const refresh = useCallback(async () => {
    try {
      const w = await api.get("/diploks/personal/wallet");
      setWallet(w.data);
      const l = await api.get("/diploks/personal/ledger?limit=10");
      setLedger(l.data);
      window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => { if (alive) await refresh(); })();
    return () => { alive = false; };
  }, [refresh]);

  if (!wallet) {
    return (
      <DashboardLayout>
        <p className="text-sm text-slate-500" data-testid="diploks-personal-wallet-loading">
          Loading your personal Diploks wallet…
        </p>
      </DashboardLayout>
    );
  }

  const bal = wallet.balance;
  const breakdown = bal?.breakdown || {};

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="personal-diploks-wallet-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Personal Wallet
            </p>
            <h1 className="mt-2 text-3xl font-bold text-[#0A192F] tracking-tight">
              My Diploks
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-xl">
              Power up personal premium tools — AI paper reviews, opening
              speech feedback, readiness reports, premium certificates and
              more.
            </p>
          </div>
          <Link
            to="/dashboard/diploks/personal/topup"
            data-testid="personal-wallet-topup-cta"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded-md hover:bg-[#B5952F]"
          >
            <Plus className="h-4 w-4" /> Top up Diploks
          </Link>
        </div>

        {/* Hero balance card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A192F] via-[#0F2C50] to-[#0F4C81] text-white p-6 sm:p-8"
          data-testid="personal-wallet-balance-hero"
        >
          <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-[#D4AF37]/15 blur-3xl pointer-events-none" />
          <div className="flex items-start justify-between gap-4 relative">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-1.5">
                <Coins className="h-3 w-3" /> Available
              </p>
              <p
                className="mt-2 text-5xl sm:text-6xl font-bold tracking-tight tabular-nums"
                data-testid="personal-wallet-balance-value"
              >
                {bal?.total_available?.toLocaleString() ?? "0"}
              </p>
              <p className="mt-1 text-xs text-slate-300">Diploks</p>
              {wallet.low_balance && (
                <span
                  data-testid="personal-wallet-low-balance"
                  className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold bg-amber-200 text-amber-900 rounded-full px-2.5 py-1"
                >
                  Low balance · Top up
                </span>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] uppercase tracking-wider text-slate-300">
                Used this lifetime
              </p>
              <p className="text-2xl font-bold tabular-nums">
                {bal?.total_used?.toLocaleString() ?? "0"}
              </p>
              {bal?.total_expiring_soon > 0 && (
                <p className="mt-1 text-[10px] text-amber-300 inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {bal.total_expiring_soon} expiring soon
                </p>
              )}
            </div>
          </div>

          {/* Breakdown chips */}
          <div className="mt-6 grid grid-cols-3 gap-2 relative">
            {[
              { key: "trial", label: "Trial", icon: Sparkles },
              { key: "bonus", label: "Bonus", icon: Sparkles },
              { key: "purchased", label: "Purchased", icon: ShieldCheck },
            ].map((b) => {
              const Icon = b.icon;
              const cell = breakdown[b.key] || { available: 0 };
              return (
                <div
                  key={b.key}
                  data-testid={`personal-wallet-breakdown-${b.key}`}
                  className="rounded-xl bg-white/5 border border-white/10 px-3 py-3"
                >
                  <p className="text-[10px] uppercase tracking-wider text-slate-300 flex items-center gap-1">
                    <Icon className="h-3 w-3" /> {b.label}
                  </p>
                  <p className="mt-0.5 text-xl font-bold tabular-nums">
                    {cell.available.toLocaleString()}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Why Diploks — premium feature inventory.  Tapping a card runs
            the central deduction engine end-to-end so users can try the
            premium experience right from the wallet page. */}
        <PremiumFeatureGrid onRefresh={refresh} />

        {/* 7-day burn meter — pure post-engine instrumentation, harmless. */}
        <div className="mt-6">
          <DiploksBurnMeter />
        </div>

        {/* Recent activity */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
              Recent activity
            </p>
            <p className="text-[10px] text-slate-400">
              Total ledger rows · {ledger.total}
            </p>
          </div>
          <div className="divide-y divide-slate-100">
            {ledger.rows.length === 0 ? (
              <p className="text-xs text-slate-500 px-5 py-6" data-testid="personal-wallet-ledger-empty">
                No Diploks activity yet — top up to get started.
              </p>
            ) : (
              ledger.rows.map((r, idx) => {
                const rowKey = r.id || `${r.created_at || ""}-${r.diploks_type || ""}-${idx}`;
                return (
                <div
                  key={rowKey}
                  data-testid={`personal-wallet-ledger-row-${rowKey}`}
                  className="px-5 py-3 flex items-center justify-between text-sm"
                >
                  <div>
                    <p className="font-semibold text-[#0A192F]">
                      {r.action || r.reason || r.diploks_type}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {String(r.created_at || "").slice(0, 16).replace("T", " · ")}
                      {r.diploks_type ? ` · ${r.diploks_type}` : ""}
                    </p>
                  </div>
                  <p
                    className={`font-bold tabular-nums ${
                      r.amount_used > 0 ? "text-rose-700" : "text-emerald-700"
                    }`}
                  >
                    {r.amount_used > 0
                      ? `-${r.amount_used}`
                      : `+${r.amount_added || 0}`}
                  </p>
                </div>
                );
              })
            )}
          </div>
        </div>

        {/* Podium Points reminder — keep the two metrics conceptually separate */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-3">
          <div className="h-8 w-8 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
            <Trophy className="h-4 w-4 text-[#0A192F]" />
          </div>
          <p className="text-xs text-slate-700 leading-relaxed">
            <span className="font-semibold text-[#0A192F]">
              Podium Points are different.
            </span>{" "}
            Podium Points are earned achievement rewards (debating, papers,
            participation). Diploks are platform credits used to unlock
            eligible premium services. They are tracked separately and never
            mixed.{" "}
            <Link
              to="/dashboard/passport"
              className="text-[#0F4C81] hover:underline inline-flex items-center gap-0.5"
            >
              View Podium Points <ArrowUpRight className="h-3 w-3" />
            </Link>
          </p>
        </div>

        {/* Compliance footer */}
        <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-[11px] text-slate-500 flex items-start gap-2">
          <Info className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
          <p>{wallet.compliance_notice}</p>
        </div>
      </div>
    </DashboardLayout>
  );
}


const PILOT_FEATURES = [
  {
    key: "position_paper_review",
    title: "AI Position Paper Review",
    body: "Get structured feedback on your paper — clauses, sourcing, depth.",
    needs: ["paperId"],
    endpoint: "/diploks/premium/paper-review",
    payload: ({ paperId }) => ({ paper_id: paperId }),
    fallbackCost: 3,
  },
  {
    key: "premium_certificate_report",
    title: "Premium Certificate Upgrade",
    body: "Designer cert with QR signature for verifiable proof.",
    needs: ["certId"],
    endpoint: "/diploks/premium/certificate-upgrade",
    payload: ({ certId }) => ({ cert_id: certId }),
    fallbackCost: 1,
  },
  {
    key: "delegate_passport_export",
    title: "Position Paper PDF Export",
    body: "Export a polished PDF version of your paper with the Diplomexa watermark.",
    needs: ["paperId"],
    endpoint: "/diploks/premium/paper-pdf-export",
    payload: ({ paperId }) => ({ paper_id: paperId }),
    fallbackCost: 2,
  },
  {
    key: "chair_feedback_report",
    title: "Chair Feedback Summary",
    body: "AI-graded committee-wide summary across delegate performance.",
    needs: ["eventId"],
    endpoint: "/diploks/premium/chair-feedback",
    payload: ({ eventId }) => ({ event_id: eventId }),
    fallbackCost: 5,
  },
];

function PremiumFeatureGrid({ onRefresh }) {
  const [costs, setCosts] = useState({});
  const [busy, setBusy] = useState(null);
  const [paperId, setPaperId] = useState(null);
  const [certId, setCertId] = useState(null);
  const [eventId, setEventId] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/diploks/feature-costs/internal").then((r) => {
      if (!alive) return;
      const m = {};
      (r.data?.rules || []).forEach((x) => { m[x.feature_key] = x.cost; });
      setCosts(m);
    }).catch(() => {});
    api.get("/papers").then((r) => {
      if (alive && r.data?.length) setPaperId(r.data[0].id);
    }).catch(() => {});
    api.get("/certificates").then((r) => {
      if (alive && r.data?.length) setCertId(r.data[0].id);
    }).catch(() => {});
    api.get("/events").then((r) => {
      if (alive && r.data?.length) setEventId(r.data[0].id);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const run = useCallback(async (feat) => {
    setBusy(feat.key);
    try {
      const consent = await requestDiploksConsent();
      if (!consent.ok) {
        toast.info("Diploks usage not activated — premium action cancelled.");
        return;
      }
      const ctx = { paperId, certId, eventId };
      const missing = feat.needs.filter((n) => !ctx[n]);
      if (missing.length) {
        toast.error(
          `Pilot demo needs: ${missing.join(", ")} (no eligible data seeded for your account yet).`
        );
        return;
      }
      const idemKey = `${feat.key}-${ctx.paperId || ctx.certId || ctx.eventId}-${Date.now()}`;
      try {
        const r = await api.post(feat.endpoint, {
          ...feat.payload(ctx),
          idempotency_key: idemKey,
        });
        const dipl = r.data?.diploks || {};
        toast.success(`${dipl.cost || feat.fallbackCost} Diploks used.`, {
          description: `Balance: ${dipl.balance_after ?? "—"}`,
        });
        window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
        if (onRefresh) onRefresh();
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || {};
        if (status === 402) {
          showLowBalance({
            ...detail,
            feature_label: feat.title,
            feature_key: feat.key,
          });
        } else if (status === 409) {
          toast.error("Diploks usage activation didn't sync. Try again.");
        } else {
          toast.error(formatApiError(e));
        }
      }
    } finally {
      setBusy(null);
    }
  }, [paperId, certId, eventId, onRefresh]);

  return (
    <div
      className="mt-8 grid sm:grid-cols-2 gap-4"
      data-testid="premium-feature-grid"
    >
      {PILOT_FEATURES.map((f) => {
        const c = costs[f.key] ?? f.fallbackCost;
        return (
          <div
            key={f.key}
            data-testid={`premium-feature-card-${f.key}`}
            className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#D4AF37] transition-colors"
          >
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-wider text-[#0F4C81] font-bold">
                Premium · Uses Diploks
              </p>
              <span
                className="text-[10px] font-bold uppercase tracking-wider bg-[#D4AF37]/15 text-[#0A192F] rounded-full px-2 py-0.5"
                data-testid={`premium-feature-cost-${f.key}`}
              >
                {c} Diploks
              </span>
            </div>
            <p className="mt-2 font-semibold text-[#0A192F]">{f.title}</p>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{f.body}</p>
            <button
              type="button"
              onClick={() => run(f)}
              disabled={busy === f.key}
              data-testid={`premium-feature-try-${f.key}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-md hover:bg-[#0F2C50] disabled:opacity-50"
            >
              <Wand2 className="h-3.5 w-3.5" />
              {busy === f.key ? "Running…" : "Try with Diploks"}
            </button>
          </div>
        );
      })}
    </div>
  );
}
